import numeral from 'numeral';
import {
    GelIcon,
    GelParagraph,
    GelSpinner,
    GelLabel,
    GelRowLayout,
    GelContainerLite,
    GelButton
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AcceptedFileTypes, AdobeClickType, AllowedFileCountPerUpload, AllowedMaxFileSize, NotificationItemIds, supportedFileTypes } from "../../../constants/constants"
import { useCookies } from "react-cookie";
import { UserContext } from "../../../common/usercontext/user.context";
import { DocumentModel } from "../../../common/models/document.model";
import styled from "@emotion/styled";
import { AdobeAnalytics } from '../../../common/analytics/adobe-analytics';
import MmcGraphQL from '../../../common/api/mmc-graphql';
import { CHECK_UPLOAD_STATUS, COMMIT_UPLOAD, UPLOAD_DOCUMENT } from '../../../graphql/mutations/graphql-upload';
import { useStagedUpload } from './useStagedUpload';
import { STAGED_FILE_STATUS, StagedFileStatus } from './stagedUploadsStore';
import { useGetClaimDetailsContent } from '../../../common/api/graphQLDataFetch';
import HorizontalRule from '../../../common/components/HorizontalRule';
import { usePolling } from '../../../common/hooks/usePolling';
import moment from 'moment';
import { UploadedDocument } from '../../../common/models/memberclaims';
import InlineAlert from '../../../common/components/InlineAlert';
import { activeUploadActions, useActiveUploadStore } from './activeUploadStore';

interface UploaderProps {
    requirement: DocumentModel;
    setAllowCollapse: (allowCollapse: boolean) => void;
}
const Uploader = ({ requirement, setAllowCollapse }: UploaderProps) => {
    const { global: { sizeBaseUnit, themeColorBackgroundDefault, themeColorBorderSelected, sizeBaseX6, themeColorIconDanger,
        themeColorTextDanger, sizeBaseX5, themeColorTextLink, themeColorTextLight, themeColorIconSuccess },
        brand: { brandColorPrimary1, brandColorPrimary1T20, brandColorPrimary1T10, brandColorPrimary2 } } = getGelTokens();

    const [isUploading, setIsUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [{ tpid }] = useCookies(['tpid']);
    const { contextData, setUserContext } = useContext(UserContext);
    const [isHover, setIsHover] = useState(false);
    const [hasUserAddedFile, setHasUserAddedFile] = useState(false);
    
    const [isSuccessfulUpload, setIsSuccessfulUpload] = useState(false);
    const [isUploadFailure, setIsUploadFailure] = useState(false);
    const [isUploadWarning, setIsUploadWarning] = useState(false);

    const isComplete = useActiveUploadStore(s => s.isUploadComplete);
    const isError = useActiveUploadStore(s => s.isError);
    const activeId = useActiveUploadStore(s => s.activeRequirementId);

    useEffect(() => {
        let isActive = (activeId === requirement.id || (!requirement.description && activeId === NotificationItemIds.OtherDocuments))
        setIsSuccessfulUpload(isActive && isComplete && !isError);
        setIsUploadWarning(isActive && isComplete && isError);
    }, [activeId, isComplete, isError]);

    const { data, isLoading } = useGetClaimDetailsContent()

    const [stagedFiles, addFile, updateFile, removeFile] = useStagedUpload(requirement?.description);

    const isTooManyFiles = stagedFiles
        .filter(f => f.status === STAGED_FILE_STATUS.UPLOADING ||
            f.status === STAGED_FILE_STATUS.SCANNING ||
            f.status === STAGED_FILE_STATUS.SUCCESS ||
            f.status === STAGED_FILE_STATUS.SANITISED)
        .length > AllowedFileCountPerUpload

    // Disable upload when:
    // 1. max file count error is showing, OR
    // 2. there are any files still uploading or scanning, OR
    // 3. there are no successfully staged (SUCCESS or SANITISED) files yet.
    const isUploadDisabled =
        isTooManyFiles ||
        stagedFiles.some(f =>
            f.status === STAGED_FILE_STATUS.UPLOADING ||
            f.status === STAGED_FILE_STATUS.SCANNING
        ) ||
        !stagedFiles.some(f =>
            f.status === STAGED_FILE_STATUS.SUCCESS ||
            f.status === STAGED_FILE_STATUS.SANITISED
        );
    
    // Show Previously uploaded files message when:
    // 1. upload is not successful yet, AND
    // 2. there are previously uploaded documents for this requirement, AND
    // 3. there are no staged files in the current upload session, AND
    // 4. user has not added any new files in the current upload session.
    // This ensures the message is not shown when user is actively uploading new files.
    const showPreviouslyUploadedMessage =
        !isUploadWarning &&
        !isSuccessfulUpload &&
        Array.isArray(requirement.associatedDocuments) &&
        requirement.associatedDocuments.length > 0 &&
        stagedFiles.length === 0 &&
        !hasUserAddedFile

    const isScanPollingActive = useCallback(
        () => stagedFiles.some(f => f.status === STAGED_FILE_STATUS.SCANNING),
        [stagedFiles]
    );
    const tick = useCallback(async (signal: AbortSignal) => {
        const scanning = stagedFiles.filter(
            f => f.status === STAGED_FILE_STATUS.SCANNING && typeof f.scanId === "number"
        );
        if (!scanning.length) return;

        MmcGraphQL<{ id: number, scanStatus: StagedFileStatus }[]>({
            query: CHECK_UPLOAD_STATUS,
            variables: { fileScanIds: scanning.map(f => f.scanId) },
        }).then(res => {
            for (const f of scanning) {
                const newStatus = res.find((x: any) => x.id === f.scanId)?.scanStatus;
                if (!newStatus) continue;
                updateFile({ ...f, status: newStatus })
            }
        }).catch(ex => console.error('Error checking upload status', ex));

    }, [stagedFiles, updateFile]);

    usePolling({ isActive: isScanPollingActive, tick, intervalMs: 3000 });

    const uploadContainerStyle = {
        borderRadius: sizeBaseUnit * 2,
        backgroundColor: brandColorPrimary1T10,
        cursor: isUploading ? "none" : "pointer",
        border: `2px dashed ${isHover ? themeColorBorderSelected : "transparent"}`
    }

    const UploadIconContainer = styled.div`
        padding-top: ${sizeBaseUnit * 4};
        height: ${sizeBaseUnit * 10}px;
        width: ${sizeBaseUnit * 10}px;
        background-color: ${isHover ? brandColorPrimary1 : themeColorBackgroundDefault};
        border-radius: 50%;
        border: 4px solid ${brandColorPrimary1T20};
        display: inline-block;
        position: relative;
    `;


    const handleUploadClick = () => {
        if (isUploading)
            return;
        AdobeAnalytics.PushClickEvent(tpid, data?.additionalDocuments?.OtherDocumentsAccordionTitle ?? "", AdobeClickType.Button)
        inputRef.current?.click();
    };

    const uploadFiles = (files: Iterable<File>) => {
        setHasUserAddedFile(true);
        activeUploadActions.consumeUploadComplete();
        setAllowCollapse(false);
        setIsUploadFailure(false);
        setIsUploadWarning(false);

        AdobeAnalytics.PushClickEvent(tpid, data?.additionalDocuments?.OtherDocumentsAccordionTitle ?? "", AdobeClickType.Button)

        for (const file of files) {
            const h = addFile({ name: file.name, size: file.size }); // status=UPLOADING

            if (!hasSupportedExtension(file.name)) {
                h.setStatus(STAGED_FILE_STATUS.NOT_SUPPORTED);
                continue;
            }

            if (file.size > AllowedMaxFileSize) {
                h.setStatus(STAGED_FILE_STATUS.FILE_SIZE_EX);
                continue;
            }

            MmcGraphQL({
                query: UPLOAD_DOCUMENT,
                variables: {
                    file: { identifier: Math.random().toString() },
                    requirements: requirement?.description ?? "",
                },
                uploads: { files: file },
            })
                .then(res => h.setScanning(res.id))
                .catch(err => h.setStatus(STAGED_FILE_STATUS.FAILED));
        }
    };

    const confirmUpload = async () => {
        setIsUploadFailure(false);
        setIsUploadWarning(false);

        var pendingFiles = stagedFiles
            .filter(f => f.status === STAGED_FILE_STATUS.SUCCESS ||
                f.status === STAGED_FILE_STATUS.SANITISED)

        if (pendingFiles.length === 0)
            return

        setIsUploading(true);

        await MmcGraphQL<UploadedDocument[]>({
            query: COMMIT_UPLOAD,
            variables: {
                fileScanIds: pendingFiles.map(f => f.scanId),
                associatedRequirements: requirement?.description ? [requirement.description] : [],
                claimId: requirement?.claimId
            }
        })
            .then(documents => {
                console.log('Successfully uploaded documents: ', documents)
                // Upload was only succesful for other members other claims
                if (!documents.some(doc => doc.claimId === requirement.claimId)) {
                    // Add documents to Claim history but not for this claim
                    AddDocumentsToContext(documents);
                    setIsUploadWarning(true);
                    activeUploadActions.setUploadComplete(true, true);
                    return;
                }
                // Add documents to claim history for all members claims
                AddDocumentsToContext(documents);
                setIsSuccessfulUpload(true);
                activeUploadActions.setUploadComplete(true);
            })
            .catch(err => {
                console.error('Commit upload error: ', err)
                setIsUploadFailure(true);
            })
            .finally(() => {
                stagedFiles.forEach(f => removeFile(f));
                setIsUploading(false);
            });
    }

    const AddDocumentsToContext = (documents: UploadedDocument[]) => {
        let updatedOtherDocuments = contextData.otherDocuments ?? [];
        let touchedClaims = false;

        documents.forEach(file => {
            if (requirement?.friendlyText && requirement.friendlyText !== '') {
                contextData.claims
                .filter(claim => claim.claimId === file.claimId)
                .forEach(claim => {
                    claim.requirements
                        .filter(req => req.friendlyText === requirement.friendlyText)
                        .forEach(req => {
                            (req.associatedDocuments ??= []).push({
                                documentName: file.documentName,
                                displayName: file.documentName,
                                documentUploadDate: moment().format("DD/MM/YYYY"),
                                sharePointDocumentIdentifier: file.documentLocation
                            });
                            touchedClaims = true;
                        });
                });
            } else {
                contextData.claims
                .filter(claim => claim.claimId === file.claimId)
                .forEach((claim) => {
                    updatedOtherDocuments.push({
                        claimId: claim.claimId,
                        documentName: file.documentName,
                        displayName: file.documentName,
                        documentUploadDate: moment().format("DD/MM/YYYY"),
                        sharePointDocumentIdentifier: file.documentLocation,
                        isAssociatedRequirementFound: false,
                        createdBy: ''
                    });
                });
            }
        });

        setUserContext({
            ...contextData,
            ...(touchedClaims ? { claims: [...contextData.claims] } : {}),           // ← bump ref
            ...(updatedOtherDocuments !== (contextData.otherDocuments ?? [])
                ? { otherDocuments: updatedOtherDocuments } : {}),
        });
    };

    type UploadFn = (files: File[]) => void;

    const extractFiles = (dt: DataTransfer): File[] => {
        const items = Array.from(dt.items ?? []);
        if (items.length) {
            return items
                .filter(i => i.kind === "file")
                .map(i => i.getAsFile())
                .filter((f): f is File => f !== null);
        }
        // Fallback for browsers that populate .files directly
        return Array.from(dt.files ?? []);
    };

    const handleDrop =
        (isUploading: boolean, uploadFiles: UploadFn) =>
            (event: React.DragEvent<HTMLElement>): void => {
                event.preventDefault();
                event.stopPropagation();
                if (isUploading) return;

                const files = extractFiles(event.dataTransfer);
                if (files.length > 0) {
                    uploadFiles(files);
                }
            };

    const hasSupportedExtension = (name: string) => {
        return supportedFileTypes.some((extension) => {
            var lastIndex = name.toLocaleLowerCase().lastIndexOf(extension);
            return lastIndex !== -1 && lastIndex + extension.length === name.length;
        });
    };

    const getIcon = (status: StagedFileStatus) => {
        if (status === STAGED_FILE_STATUS.UPLOADING || status === STAGED_FILE_STATUS.SCANNING)
            return <GelSpinner small overlay={false} />;
        else if (
            status === STAGED_FILE_STATUS.VULNERABLE || status === STAGED_FILE_STATUS.FAILED ||
            status === STAGED_FILE_STATUS.NOT_SUPPORTED || status === STAGED_FILE_STATUS.FILE_SIZE_EX
        )
            return <GelIcon
                name="AlertTriangle"
                width={sizeBaseX6}
                height={sizeBaseX6}
                color={themeColorIconDanger}
            />;
        else if (status === STAGED_FILE_STATUS.SUCCESS || status === STAGED_FILE_STATUS.SANITISED) {
            return <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill={themeColorIconSuccess}>
                <path fillRule="evenodd" clipRule="evenodd" d="M4.24923 5.14401C5.0632 2.17914 7.7764 0.000976562 11 0.000976562C14.2236 0.000976562 16.9368 2.17914 17.7508 5.14401C20.1861 5.71059 22 7.89347 22 10.501C22 12.4089 21.0277 14.0892 19.5563 15.0742C19.0973 15.3814 18.4762 15.2584 18.169 14.7995C17.8618 14.3405 17.9848 13.7194 18.4437 13.4122C19.3842 12.7826 20 11.7135 20 10.501C20 8.67876 18.6067 7.18082 16.828 7.01608C16.375 6.97412 16.0073 6.63156 15.9335 6.18256C15.5436 3.81065 13.4822 2.00098 11 2.00098C8.51785 2.00098 6.45643 3.81065 6.0665 6.18256C5.99268 6.63156 5.62505 6.97412 5.17196 7.01608C3.39329 7.18082 2 8.67876 2 10.501C2 11.7135 2.61575 12.7826 3.55626 13.4122C4.01521 13.7194 4.13822 14.3405 3.83101 14.7995C3.52379 15.2584 2.90269 15.3814 2.44374 15.0742C0.972277 14.0892 0 12.4089 0 10.501C0 7.89347 1.8139 5.71059 4.24923 5.14401ZM10 12.4152L7.70711 14.7081C7.31658 15.0986 6.68342 15.0986 6.29289 14.7081C5.90237 14.3176 5.90237 13.6844 6.29289 13.2939L10.2929 9.29387C10.6834 8.90335 11.3166 8.90335 11.7071 9.29387L15.7071 13.2939C16.0976 13.6844 16.0976 14.3176 15.7071 14.7081C15.3166 15.0986 14.6834 15.0986 14.2929 14.7081L12 12.4152V19.001C12 19.5533 11.5523 20.001 11 20.001C10.4477 20.001 10 19.5533 10 19.001V12.4152Z" fill={themeColorIconSuccess} />
            </svg>;
        }
    };

    const getMessage = (status: StagedFileStatus, size: number) => {
        let formattedSize = numeral(size).format('0 b');

        if (status === STAGED_FILE_STATUS.UPLOADING || status === STAGED_FILE_STATUS.SCANNING)
            return <GelParagraph>Scanning...</GelParagraph>;
        else if (status === STAGED_FILE_STATUS.VULNERABLE || status === STAGED_FILE_STATUS.FAILED)
            return <GelParagraph style={{ color: themeColorIconDanger }}>
                {data?.validationErrors?.unSafeFileError} | Failed
            </GelParagraph>;
        else if (status === STAGED_FILE_STATUS.FILE_SIZE_EX)
            return <GelParagraph style={{ color: themeColorIconDanger }}>
                {data?.validationErrors?.maxSizeError} | Failed
            </GelParagraph>;
        else if (status === STAGED_FILE_STATUS.NOT_SUPPORTED)
            return <GelParagraph style={{ color: themeColorIconDanger }}>
                {data?.validationErrors?.unSupportedFileError} | Failed
            </GelParagraph>;
        else if (status === STAGED_FILE_STATUS.SUCCESS || status === STAGED_FILE_STATUS.SANITISED)
            return <GelParagraph>{formattedSize} | Pending upload</GelParagraph>;
    }

    if (isLoading) return (<GelSpinner medium overlay />)

    return (
        <div data-upload-root="true">
            <GelContainerLite
                style={uploadContainerStyle}
                onClick={handleUploadClick}
                onMouseEnter={() => !isUploading && setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop(isUploading, uploadFiles)}
                onDragEnter={e => {
                    e.preventDefault();
                    if (!isUploading) setIsHover(true);
                }}
                onDragLeave={() => setIsHover(false)}
            >
                <div style={{
                    paddingTop: "16px",
                    paddingBottom: "16px"
                }}>
                    <GelRowLayout alignment="center">
                        <UploadIconContainer>
                            <GelIcon
                                name="Upload"
                                style={{
                                    width: sizeBaseX5,
                                    position: "absolute",
                                    top: "50%", left: "50%",
                                    transform: "translate(-50%, -50%)"
                                }}
                                color={isHover ? themeColorBackgroundDefault : brandColorPrimary2}
                            />
                        </UploadIconContainer>
                        <GelParagraph style={{ color: themeColorTextLink, textAlign: "center" }}>
                            {data?.files?.SelectFiles}
                        </GelParagraph>
                        <GelParagraph
                            style={{ color: themeColorTextLight, textAlign: "center" }}>
                            {" "}
                            {data?.files?.DragFiles}
                        </GelParagraph>
                        <GelParagraph
                            style={{ color: '#667085', textAlign: "center", fontSize: '12px' }}>
                            {" "}
                            {data?.files?.MaxFileSizeAndSupportedTypes}
                        </GelParagraph>
                    </GelRowLayout>
                </div>
            </GelContainerLite>

            {isTooManyFiles &&
                <GelParagraph style={{ color: themeColorTextDanger }}>
                    {data?.validationErrors.maxFileCountError}
                </GelParagraph>
            }

            {stagedFiles.length > 0 && 
                <GelParagraph style={{ paddingTop: '8px' }} 
                    dangerouslySetInnerHTML={{ __html: data?.files?.UploadInstructionsMessage || '' }}>
                </GelParagraph>
            }

            <div style={{ paddingTop: "8px", display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div>
                    {stagedFiles.map((file, index) => (
                        <Fragment key={file.id}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}>
                                <div style={{ width: '24px', height: '24px' }}>
                                    {getIcon(file.status)}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, paddingLeft: '8px', gap: '2px' }}>
                                    <GelLabel style={{ fontWeight: 500 }}>
                                        {file.name}
                                    </GelLabel>
                                    {getMessage(file.status, file.size)}
                                </div>
                                {file.status !== STAGED_FILE_STATUS.FAILED &&
                                    file.status !== STAGED_FILE_STATUS.FILE_SIZE_EX &&
                                    file.status !== STAGED_FILE_STATUS.NOT_SUPPORTED &&
                                    file.status !== STAGED_FILE_STATUS.VULNERABLE &&
                                    !isUploading &&
                                    <div onClick={() => removeFile(file)} id={`${file.name}.remove`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 24,
                                            height: 24,
                                            lineHeight: 0,
                                            borderRadius: 4,
                                            border: `1px solid ${brandColorPrimary1}`,
                                            background: '#FFF',
                                            boxShadow: '0 0 0 0',
                                            padding: 0,
                                            cursor: 'pointer',
                                        }}>
                                        <GelIcon
                                            name={'ExitCircle'}
                                            width={16}
                                            height={16}
                                            color={brandColorPrimary1}
                                        />
                                    </div>}
                            </div>
                            {index < stagedFiles.length - 1 && <HorizontalRule />}
                        </Fragment>
                    ))}
                </div>
                <input
                    type="file"
                    accept={AcceptedFileTypes}
                    ref={inputRef}
                    onChange={e => e.target.files && uploadFiles(e.target.files)}
                    style={{ display: "none" }}
                    aria-label="document"
                    multiple
                />

                {isSuccessfulUpload && 
                    <InlineAlert message={data?.files?.SuccessfulUploadMessage || ''} variant={"success"} />}
                
                {showPreviouslyUploadedMessage && 
                    <InlineAlert message={data?.files?.PreviouslyUploadedMessage || ''} variant={"info"} />}
                
                {isUploadFailure &&
                    <InlineAlert message={data?.files?.UploadFailureMessage || ''} variant={"danger"} />}
                
                {isUploadWarning &&
                    <InlineAlert message={data?.files?.UploadedOnlyToOtherClaimsMessage || ''} variant={"warning"} />}

                {stagedFiles?.length > 0 &&
                    <GelButton primary large disabled={isUploadDisabled} loading={isUploading} onClick={confirmUpload} style={{ width: '100%', marginBottom: '16px' }}>
                        {data?.files?.ConfirmUploadButtonText}
                    </GelButton>}
            </div>
        </div>
    );
};

export default Uploader;